import { useEffect, useRef, useState } from "react";

/**
 * CinematicCursor - Curseur personnalisé premium VEA STUDIO
 * - Petit point rouge bordeaux au centre
 * - Anneau or qui suit avec un léger délai (lag effect)
 * - Grossit sur les éléments interactifs (hover)
 * - Disparaît sur mobile (touch devices)
 */
export default function CinematicCursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);

  const pos = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const rafId = useRef<number>(0);

  useEffect(() => {
    // Ne pas afficher sur les appareils tactiles
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const onMove = (e: MouseEvent) => {
      pos.current = { x: e.clientX, y: e.clientY };
      if (!visible) setVisible(true);
    };

    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    const onMouseDown = () => setClicked(true);
    const onMouseUp = () => setClicked(false);

    // Détecter les éléments interactifs
    const onHoverStart = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest("a") ||
        target.closest("button") ||
        target.closest("[role='button']") ||
        target.closest("input") ||
        target.closest("textarea") ||
        target.closest("select") ||
        target.tagName === "A" ||
        target.tagName === "BUTTON" ||
        target.tagName === "INPUT"
      ) {
        setHovered(true);
      } else {
        setHovered(false);
      }
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mousemove", onHoverStart, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mouseup", onMouseUp);

    // Animation loop - anneau réactif (suit de près, léger lissage seulement)
    const animate = () => {
      // Facteur élevé = l'anneau colle au curseur (avant : 0.12 = trop lent)
      ringPos.current.x += (pos.current.x - ringPos.current.x) * 0.55;
      ringPos.current.y += (pos.current.y - ringPos.current.y) * 0.55;

      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px) translate(-50%, -50%)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ringPos.current.x}px, ${ringPos.current.y}px) translate(-50%, -50%)`;
      }

      rafId.current = requestAnimationFrame(animate);
    };

    rafId.current = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mousemove", onHoverStart);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mouseup", onMouseUp);
      cancelAnimationFrame(rafId.current);
    };
  }, [visible]);

  // SSR-safe : on rend TOUJOURS le curseur (1er rendu identique serveur/client,
  // pas de mismatch d'hydratation), et on le masque sur pointeur grossier
  // (mobile/tactile) PAR CSS. Auparavant un `return null` conditionne a
  // matchMedia AU RENDU divergeait du SSR. Voir le fix handivia (MagneticCursor).
  return (
    <>
      {/* Masquer le curseur natif via CSS global ; sur mobile, on restaure le
          curseur natif et on cache les elements du curseur personnalise. */}
      <style>{`
        body { cursor: none !important; }
        a, button, [role="button"], input, textarea, select { cursor: none !important; }
        @media (pointer: coarse) {
          body, a, button, [role="button"], input, textarea, select { cursor: auto !important; }
          .vea-cine-cursor { display: none !important; }
        }
      `}</style>

      {/* Point central - rouge bordeaux */}
      <div
        ref={dotRef}
        className="vea-cine-cursor"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: clicked ? "6px" : "8px",
          height: clicked ? "6px" : "8px",
          borderRadius: "50%",
          background: "var(--vea-rouge-bordeaux)",
          pointerEvents: "none",
          zIndex: 99999,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.3s ease, width 0.15s ease, height 0.15s ease",
          willChange: "transform",
          boxShadow: "0 0 6px rgba(166,30,45,0.6)",
        }}
      />

      {/* Anneau suiveur - or */}
      <div
        ref={ringRef}
        className="vea-cine-cursor"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: hovered ? "48px" : clicked ? "20px" : "32px",
          height: hovered ? "48px" : clicked ? "20px" : "32px",
          borderRadius: "50%",
          border: `1px solid ${hovered ? "rgba(201,168,76,0.8)" : "rgba(201,168,76,0.4)"}`,
          pointerEvents: "none",
          zIndex: 99998,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.3s ease, width 0.18s cubic-bezier(0.16,1,0.3,1), height 0.18s cubic-bezier(0.16,1,0.3,1), border-color 0.2s ease",
          willChange: "transform",
          background: hovered ? "rgba(201,168,76,0.04)" : "transparent",
        }}
      />
    </>
  );
}
